import React, { useState, useEffect, useCallback } from 'react';
import { ScrollText, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import apiService from '../../apiService';

const SERVICE_LABELS = {
  finance: { label: 'Finance', bg: '#ECFDF5', color: '#10B981' },
  validation: { label: 'Validation', bg: '#FEE9E2', color: '#4338ca' },
  governance: { label: 'Gouvernance', bg: '#fef9c3', color: '#ca8a04' },
  ops_transport: { label: 'Transport', bg: '#ffedd5', color: '#ea580c' },
  ops_delivery: { label: 'Livraison', bg: '#cffafe', color: '#0891b2' },
  support: { label: 'Support', bg: '#f3e8ff', color: '#FF5500' },
  it: { label: 'IT', bg: '#E0E7FF', color: '#4338CA' },
};

function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AdminAuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [serviceFilter, setServiceFilter] = useState('');
  const [actionSearch, setActionSearch] = useState('');
  const [adminFilter, setAdminFilter] = useState('');
  const [admins, setAdmins] = useState([]); // admins présents au journal
  const limit = 30;

  useEffect(() => {
    apiService.get('/admin/audit-logs/admins', { userType: 'admin' })
      .then(res => setAdmins(res.admins || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page, limit });
      if (serviceFilter) qs.set('service', serviceFilter);
      if (actionSearch.trim()) qs.set('action', actionSearch.trim());
      if (adminFilter) qs.set('admin_id', adminFilter);
      const res = await apiService.get(`/admin/audit-logs?${qs}`, { userType: 'admin' });
      setLogs(res.logs || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error('load audit logs:', e);
    } finally {
      setLoading(false);
    }
  }, [page, serviceFilter, actionSearch, adminFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [serviceFilter, actionSearch, adminFilter]);

  const pages = Math.ceil(total / limit);

  return (
    <div style={{ padding: '24px', maxWidth: '1300px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 800, margin: 0, color: '#12151A', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ScrollText size={22} /> Journal d'audit
        </h1>
        <p style={{ margin: '4px 0 0', color: '#5C636E', fontSize: '14px' }}>Traçabilité des actions sensibles (Finance &amp; Validation)</p>
      </div>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
          <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9AA0A8' }} />
          <input type="text" value={actionSearch} onChange={e => setActionSearch(e.target.value)} placeholder="Rechercher une action (ex. driver.approve)"
            style={{ width: '100%', padding: '9px 12px 9px 34px', borderRadius: '10px', border: '1px solid #E9EBEF', fontSize: '14px', boxSizing: 'border-box' }} />
        </div>
        <select value={adminFilter} onChange={e => setAdminFilter(e.target.value)} style={{ padding: '9px 12px', borderRadius: '10px', border: adminFilter ? '1.5px solid #F54119' : '1px solid #E9EBEF', fontSize: '14px', cursor: 'pointer', maxWidth: '260px' }}>
          <option value="">Tous les admins</option>
          {admins.map(a => <option key={a.admin_id} value={a.admin_id}>{a.admin_name} ({a.count})</option>)}
        </select>
        <select value={serviceFilter} onChange={e => setServiceFilter(e.target.value)} style={{ padding: '9px 12px', borderRadius: '10px', border: '1px solid #E9EBEF', fontSize: '14px', cursor: 'pointer' }}>
          <option value="">Tous les services</option>
          {Object.entries(SERVICE_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        {(adminFilter || serviceFilter || actionSearch) && (
          <button onClick={() => { setAdminFilter(''); setServiceFilter(''); setActionSearch(''); }}
            style={{ padding: '9px 14px', borderRadius: '10px', border: 'none', background: '#F4F5F7', color: '#414852', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            Effacer les filtres
          </button>
        )}
      </div>

      <div style={{ background: '#fff', borderRadius: '14px', overflow: 'hidden', border: '1px solid #E9EBEF' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#FFFFFF', borderBottom: '1px solid #E9EBEF' }}>
              {['Date', 'Admin', 'Service', 'Action', 'Cible', 'Détails'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 700, color: '#9AA0A8', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: '48px', color: '#9AA0A8' }}>Chargement...</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: '48px', color: '#9AA0A8' }}>Aucune entrée</td></tr>
            ) : logs.map(l => {
              const sv = SERVICE_LABELS[l.service] || { label: l.service || '—', bg: '#F4F5F7', color: '#5C636E' };
              return (
                <tr key={l.log_id} style={{ borderBottom: '1px solid #F4F5F7' }}>
                  <td style={{ padding: '10px 16px', fontSize: '12px', color: '#5C636E', whiteSpace: 'nowrap' }}>{fmtDateTime(l.created_at)}</td>
                  <td style={{ padding: '10px 16px', fontSize: '13px', fontWeight: 600 }}>
                    <span onClick={() => setAdminFilter(l.admin_id)} title="Voir uniquement les actions de cet admin"
                      style={{ cursor: 'pointer', borderBottom: '1px dashed #C9CDD3' }}>
                      {l.admin_name || l.admin_id}
                    </span>
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    <span style={{ background: sv.bg, color: sv.color, padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600 }}>{sv.label}</span>
                  </td>
                  <td style={{ padding: '10px 16px', fontSize: '13px', fontFamily: 'monospace', color: '#12151A' }}>{l.action}</td>
                  <td style={{ padding: '10px 16px', fontSize: '12px', color: '#5C636E' }}>{l.target_type}{l.target_id ? ` #${String(l.target_id).slice(-8)}` : ''}</td>
                  <td style={{ padding: '10px 16px', fontSize: '12px', color: '#5C636E', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {l.details && Object.keys(l.details).length > 0 ? JSON.stringify(l.details) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={{ background: page > 1 ? '#F4F5F7' : '#FFFFFF', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: page > 1 ? 'pointer' : 'default' }}><ChevronLeft size={16} /></button>
          <span style={{ fontSize: '14px', color: '#414852' }}>Page {page} / {pages} ({total})</span>
          <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages} style={{ background: page < pages ? '#F4F5F7' : '#FFFFFF', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: page < pages ? 'pointer' : 'default' }}><ChevronRight size={16} /></button>
        </div>
      )}
    </div>
  );
}
